import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import ChatContact from './ChatContact';
import Translate from '../../../../../components/Translate';
import '../chat-css/ChatCard.css';
import ForumIcon from "../../../../../assets/icons/ForumIcon";
import {fromBackendDate} from '../../../../../utils/DateUtils';

const ConversationList = (props) => {
    const {
        conversationList,
        selectConversation,
        selectedConversation,
        id,
        contactsNameSpace,
        user,
        isSupervisor,
        openForwardDialog,
        forwardConversation,
        company,
        loadOlderMessages,
        clearSelectedConversationFromStore,
    } = props;

    const listRef = useRef(null);
    const railRef = useRef(null);
    const itemRefs = useRef({});
    const prevPositions = useRef({});
    const prevOrder = useRef([]);
    const seen = useRef({});
    /*
     * Switching tabs mounts a fresh list, so every chat in it would count as
     * newly arrived and the whole column would fade in at once - which is what
     * made the selected card's edge flicker. Only chats that turn up while the
     * list is already on screen animate.
     */
    const hasRendered = useRef(false);

    /*
     * Chats on their way out. React drops an element the moment it leaves the
     * list, so a chat that is closed or taken by someone else would blink out
     * and pull the cards below it up by a card's height. Their ids are held for
     * the length of the collapse and then let go.
     */
    const [leaving, setLeaving] = useState([]);

    let sortedConversations = [...conversationList];
    sortedConversations.sort((a, b) => {
        const timeA = a.date
            ? fromBackendDate(a.date).getTime()
            : fromBackendDate(a.lastModifiedAt).getTime();

        const timeB = b.date
            ? fromBackendDate(b.date).getTime()
            : fromBackendDate(b.lastModifiedAt).getTime();

        return timeB - timeA;
    });

    const ids = sortedConversations.map(c => c.id);

    useEffect(() => {
        const gone = prevOrder.current.filter(prevId => ids.indexOf(prevId) === -1);
        if (gone.length === 0) return undefined;

        gone.forEach((g) => {
            const node = itemRefs.current[g];
            if (node) node.className = 'chat-card-leave';
        });

        setLeaving(prev => prev.concat(gone.filter(g => prev.indexOf(g) === -1)));

        const timer = setTimeout(() => {
            setLeaving(prev => prev.filter(l => gone.indexOf(l) === -1));
            gone.forEach((g) => {
                delete itemRefs.current[g];
                delete seen.current[g];
            });
        }, 240);

        return () => clearTimeout(timer);
    }, [ids.join(',')]);

    /*
     * The brand edge of the selected chat, as one element that travels to it -
     * so the eye can follow where the selection went instead of finding it
     * switched on somewhere else.
     */
    useLayoutEffect(() => {
        const rail = railRef.current;
        if (!rail) return;

        const node = itemRefs.current[selectedConversation.id];
        if (!node || ids.indexOf(selectedConversation.id) === -1) {
            rail.classList.add('hidden');
            return;
        }

        rail.classList.remove('hidden');
        rail.style.height = node.offsetHeight + 'px';
        rail.style.transform = 'translateY(' + node.offsetTop + 'px)';
    });

    useLayoutEffect(() => {
        const newOrder = sortedConversations.map(c => c.id);

        const orderIsSame = newOrder.length === prevOrder.current.length &&
            newOrder.every((cid, index) => cid === prevOrder.current[index]);

        const newPositions = {};
        sortedConversations.forEach((conv) => {
            const domNode = itemRefs.current[conv.id];
            if (domNode) {
                newPositions[conv.id] = domNode.getBoundingClientRect();
            }
        });

        if (!orderIsSame) {
            sortedConversations.forEach((conv) => {
                const domNode = itemRefs.current[conv.id];
                if (!domNode) return;

                const prevRect = prevPositions.current[conv.id];
                const newRect = newPositions[conv.id];

                if (prevRect && newRect) {
                    const deltaY = prevRect.top - newRect.top;

                    if (deltaY !== 0) {
                        domNode.style.transition = 'none';
                        domNode.style.transform = `translateY(${deltaY}px)`;

                        domNode.offsetHeight;

                        domNode.style.transition = 'transform 300ms ease';
                        domNode.style.transform = '';
                    }
                }
            });
        }

        prevPositions.current = newPositions;
        prevOrder.current = newOrder;
        hasRendered.current = true;
    }, [sortedConversations]);

    if (sortedConversations.length === 0 && leaving.length === 0) {
        return (
            <div className="chat-empty chat-empty--compact">
                <span className="chat-empty__icon">
                    <ForumIcon width={24} height={24} />
                </span>
                <div>
                    <div className="chat-empty__title"><Translate text="noChatsHere"/></div>
                    <p className="chat-empty__note"><Translate text="noChatsHereNote"/></p>
                </div>
            </div>
        );
    }

    return (
        <ul id={id} className="list ul-list chat-contacts chat-list" ref={listRef}>
            <span className="chat-rail hidden" ref={railRef}/>

            {sortedConversations.map((conversation) => {
                const isSelected = selectedConversation.id === conversation.id;
                /* the first time a chat is drawn, and only then, it slides in */
                const isNew = hasRendered.current && !seen.current[conversation.id];
                seen.current[conversation.id] = true;

                return (
                    <div
                        key={conversation.id}
                        className={isNew ? 'chat-card-enter' : ''}
                        ref={(el) => {
                            if (el) itemRefs.current[conversation.id] = el;
                        }}
                    >
                        <ChatContact
                            openForwardDialog={openForwardDialog}
                            conversation={conversation}
                            contactNameSpace={contactsNameSpace}
                            selectConversation={selectConversation}
                            isSelected={isSelected}
                            user={user}
                            isSupervisor={isSupervisor}
                            forwardConversation={forwardConversation}
                            company={company}
                            loadOlderMessages={loadOlderMessages}
                            clearSelectedConversationFromStore={clearSelectedConversationFromStore}
                        />
                    </div>
                );
            })}
        </ul>
    );
};

export default ConversationList;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/ConversationList.jsx